import { type MeDto, PASSWORD_MIN_LENGTH, type PublicInvitationDto } from "@clinic/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, api } from "../api";
import { fieldErrors, formValues, hasFieldErrors } from "../forms";
import { LanguageSwitcher, useT } from "../i18n";
import { useSessionMutation } from "../session";
import { Alert, Button, Card, Field, FullPageSpinner, Logo } from "../ui";

/** Accepting a team invitation from the emailed link (CB-23). */
export function InvitePage() {
  const { token = "" } = useParams();
  const { t, tError } = useT();
  const navigate = useNavigate();
  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api<PublicInvitationDto>("GET", `/public/invitations/${token}`),
    retry: false,
  });
  const accept = useSessionMutation((input: Record<string, string>) =>
    api<MeDto>("POST", `/public/invitations/${token}/accept`, input),
  );
  const fields = fieldErrors(accept.error);

  let body: React.ReactNode;
  if (invitation.isPending) return <FullPageSpinner />;
  if (invitation.isError) {
    body = (
      <Alert>
        {invitation.error instanceof ApiError && invitation.error.status === 404
          ? t("invite.not_found")
          : tError(invitation.error)}
      </Alert>
    );
  } else if (invitation.data.state !== "open") {
    body = <Alert>{t("invite.not_open")}</Alert>;
  } else {
    const i = invitation.data;
    body = (
      <>
        <h1 className="text-xl font-semibold">{t("invite.title", { clinic: i.clinicName })}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {t("invite.text", { clinic: i.clinicName, role: t(`team.role_${i.role}`).toLowerCase() })}
        </p>
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => accept.mutate(formValues(e), { onSuccess: () => navigate("/app") })}
        >
          {accept.error && !hasFieldErrors(accept.error) ? <Alert>{tError(accept.error)}</Alert> : null}
          <Field label={t("invite.email")} value={i.email} disabled readOnly />
          {!i.userExists ? (
            <Field label={t("invite.your_name")} name="fullName" autoComplete="name" required error={fields.fullName} />
          ) : null}
          <Field
            label={t("invite.password")}
            name="password"
            type="password"
            autoComplete={i.userExists ? "current-password" : "new-password"}
            required
            minLength={i.userExists ? 1 : PASSWORD_MIN_LENGTH}
            hint={
              i.userExists
                ? t("invite.password_existing_hint")
                : t("invite.password_new_hint", { n: PASSWORD_MIN_LENGTH })
            }
            error={fields.password}
          />
          <Button className="w-full" busy={accept.isPending}>
            {t("invite.accept")}
          </Button>
        </form>
      </>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="mb-8 flex w-full max-w-md items-center justify-between">
        <Link to="/">
          <Logo />
        </Link>
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">{body}</Card>
    </main>
  );
}
